<template>
	<div>
		<div class="column q-col-gutter-md">
			<div class="col" v-if="user.zohoAuthenticated">
				<customer-select v-model="editedWatchlistProps.customer" :customer-name="watchlist.customerName" :error="errors.customer"/>
			</div>

			<div class="col">
				<q-input v-model="editedWatchlistProps.name"
						 :label="$t('name')"
						 :error="errors.name"
						 :error-message="$t('error.watchlistProps.name')"
				/>
			</div>

			<div class="col">
				<user-select v-model="editedWatchlistProps.seller" :initial-user="initialSeller"/>
			</div>

			<div class="col">
				<q-select v-model="zohoLanguage"
						  :options="languageOptions"
						  :label="$t('label.language')"
						  fill-input
						  map-options
						  emit-value
				/>
			</div>

			<div class="col" v-if="watchlist.totalArea">
				<div>
					{{ $t('resultArea') }}: {{ watchlist.totalArea }}
				</div>
			</div>

			<div class="col" v-if="!dynamic">
				<q-select v-model="currency"
						  :options="currencyOptions"
						  :label="$t('currency')"
						  fill-input
						  map-options
						  emit-value
				/>
			</div>

			<template v-if="!this.watchlist.uid">
				<div class="col">
					<q-select v-model="product"
							  :options="productOptions"
							  :label="$t('product')"
							  fill-input
							  map-options
							  emit-value
					/>
				</div>

				<div class="col">
					<q-select v-model="dynamic"
							  :options="watchlistTypeOptions"
							  :label="$t('label.watchlistType')"
							  fill-input
							  map-options
							  emit-value
					/>
				</div>
			</template>
		</div>

		<q-separator spaced/>

		<div class="row">
			<div class="col">
				<q-btn @click="cancel" :label="$t('button.cancel')" flat/>
			</div>
			<div class="col">
				<q-btn @click="save" :label="this.watchlist.uid ? $t('button.update') : $t('button.create')" :loading="loading" color="primary"/>
			</div>
		</div>
	</div>
</template>

<script>
	import {mapGetters} from "vuex";
	import {CURRENCIES, LANGUAGES, PRODUCT_TYPES} from "@/common/constants";
	import {CREATE_WATCHLIST, UPDATE_WATCHLIST} from "@/store/actions.type";
	import {HIDE_DIALOG} from "@/store/mutations.type";
	import CustomerSelect from "@/components/zoho/CustomerSelect";
	import UserSelect from "@/components/form/UserSelect";

	export default {
		name: "WatchlistSettings",
		components: {UserSelect, CustomerSelect},
		props: ['props'],
		data() {
			return {
				editedWatchlistProps: {},
				errors: {},
				zohoLanguage: LANGUAGES[0],
				currency: 'EUR',
				product: PRODUCT_TYPES.Veneer,
				dynamic: 0,
				loading: false,
				watchlist: this.props.watchlist
			}
		},
		mounted() {
			// copies current properties into new object, excludes properties that should not be updated through this component
			const trustedProperties = ['name', 'currency', 'zohoCustomer', 'zohoLanguage'];
			this.editedWatchlistProps = {};

			for(let prop of trustedProperties) {
				if(this.watchlist.hasOwnProperty(prop)) {
					this.editedWatchlistProps[prop] = this.watchlist[prop];
				}
			}

			if (this.watchlist.seller) {
				this.editedWatchlistProps.seller = this.watchlist.seller.uid
			}

			this.zohoLanguage = this.watchlist.zohoLanguage || this.zohoLanguage;
			this.currency = this.watchlist.currency || this.currency;
			this.product = this.watchlist.product || this.product;
			this.dynamic = this.watchlist.dynamic ? 1 : 0;
		},
		computed: {
			...mapGetters(['user']),
			languageOptions() {
				const languageOptions = [];
				for (let key in LANGUAGES) {
					languageOptions.push({label: this.$t('label.languages.' + LANGUAGES[key]), value: LANGUAGES[key]})
				}
				return languageOptions;
			},
			currencyOptions() {
				const currencyOptions = [];
				for (let key in CURRENCIES) {
					currencyOptions.push({label: CURRENCIES[key], value: key});
				}
				return currencyOptions;
			},
			productOptions() {
				const productOptions = [];
				for (let key in PRODUCT_TYPES) {
					productOptions.push({label: this.$t('label.productTypes.' + PRODUCT_TYPES[key]), value: PRODUCT_TYPES[key]});
				}
				return productOptions;
			},
			watchlistTypeOptions() {
				return [
					{label: this.$t('static'), value: 0},
					{label: this.$t('dynamic'), value: 1}
				];
			},
			initialSeller() {
				return this.watchlist.seller || this.user;
			}
		},
		methods: {
			cancel() {
				this.$store.commit(HIDE_DIALOG);
			},
			save() {
				if (this.watchlist.uid) {
					this.update();
				} else {
					this.create();
				}
			},
			getWatchlistProps() {
				return {
					...this.editedWatchlistProps,
					zohoLanguage: this.zohoLanguage,
					currency: this.currency,
					product: this.product,
					dynamic: this.dynamic
				};
			},
			validate() {
				this.errors = {
					customer: this.user.zohoAuthenticated ? (!this.editedWatchlistProps.zohoCustomer && !this.editedWatchlistProps.customer) : false,
					name: !this.editedWatchlistProps.name
				};

				return !Object.values(this.errors).includes(true);
			},
			async update() {
				if(!this.validate()) {
					return;
				}

				this.loading = true;

				await this.$store.dispatch(UPDATE_WATCHLIST, {
					object: this.watchlist, props: this.getWatchlistProps()
				});

				this.loading = false;

				this.$store.commit(HIDE_DIALOG);
			},
			async create() {
				if(!this.validate()) {
					return;
				}

				this.loading = true;

				const newWatchlist = await this.$store.dispatch(CREATE_WATCHLIST, this.getWatchlistProps());

				this.loading = false;

				this.$store.commit(HIDE_DIALOG);

				await this.$router.push({name: 'watchlistDetail', params: {id: newWatchlist.uid, watchlist: newWatchlist}});
			}
		}
	}
</script>